import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { hasAdminCapability, useAuth } from '@services/auth';
import { getUsers, type UserResponse } from '@services/users';
import { useAdminViewAs, type AdminViewAsTarget } from '../lib/adminViewAs';
import { useI18n } from '../i18n';

/** Garden Seekers sidebar → seekers first, then company, then gardener (admin last). */
const ROLE_RANK_SEEKER_SECTION: Record<UserResponse['role'], number> = {
	seeker: 0,
	company: 1,
	gardener: 2,
	admin: 3
};

/** Gardener sidebar (and rest of app outside seeker routes) → company, gardener, seeker (admin last). */
const ROLE_RANK_GARDENER_SECTION: Record<UserResponse['role'], number> = {
	company: 0,
	gardener: 1,
	seeker: 2,
	admin: 3
};

function sortUsersForAdminDropdown(list: UserResponse[], seekerSection: boolean): UserResponse[] {
	const rank = seekerSection ? ROLE_RANK_SEEKER_SECTION : ROLE_RANK_GARDENER_SECTION;
	return [...list].sort((a, b) => {
		const ra = rank[a.role] ?? 99;
		const rb = rank[b.role] ?? 99;
		if (ra !== rb) return ra - rb;
		const na = String(a.name || a.email || '').toLowerCase();
		const nb = String(b.name || b.email || '').toLowerCase();
		const c = na.localeCompare(nb);
		if (c !== 0) return c;
		return a.id - b.id;
	});
}

export default function AdminViewAsStrip() {
	const location = useLocation();
	const { user } = useAuth();
	const { viewAsTarget, setViewAsTarget, clearViewAs } = useAdminViewAs();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;

	const [users, setUsers] = useState<UserResponse[]>([]);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [query, setQuery] = useState('');
	const [roleFilter, setRoleFilter] = useState<'all' | UserResponse['role']>('all');
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (user?.role !== 'admin' || !hasAdminCapability(user, 'admin_view_as')) return;
		let cancelled = false;
		getUsers()
			.then((list) => {
				if (!cancelled) {
					setUsers(Array.isArray(list) ? list : []);
					setLoadError(null);
				}
			})
			.catch((e) => {
				if (!cancelled) {
					setUsers([]);
					setLoadError(e instanceof Error ? e.message : 'Failed to load users');
				}
			});
		return () => {
			cancelled = true;
		};
	}, [user]);

	const seekerSidebarActive = location.pathname.startsWith('/app/seeker');

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		let list =
			q === ''
				? users
				: users.filter(
						(u) =>
							String(u.name || '')
								.toLowerCase()
								.includes(q) ||
							String(u.email || '')
								.toLowerCase()
								.includes(q) ||
							String(u.id).includes(q)
					);
		if (roleFilter !== 'all') {
			list = list.filter((u) => u.role === roleFilter);
		}
		return sortUsersForAdminDropdown(list, seekerSidebarActive).slice(0, 80);
	}, [users, query, roleFilter, seekerSidebarActive]);

	if (!user || user.role !== 'admin' || !hasAdminCapability(user, 'admin_view_as')) {
		return null;
	}

	function pick(u: UserResponse) {
		const t: AdminViewAsTarget = {
			id: u.id,
			name: u.name || u.email || `User ${u.id}`,
			email: u.email || '',
			role: u.role
		};
		setViewAsTarget(t);
		setOpen(false);
		setQuery('');
	}

	return (
		<div className="border-bottom bg-body-tertiary px-3 py-2 small">
			<div className="d-flex flex-wrap align-items-center gap-2">
				<span className="text-muted fw-semibold text-uppercase" style={{ letterSpacing: '0.04em' }}>
					{copy('Admin view', 'Admin-Ansicht', 'Admin prikaz')}
				</span>
				{viewAsTarget && (
					<span className="badge text-bg-info">
						{copy('Viewing as', 'Angemeldet als', 'Pregled kao')}: {viewAsTarget.name} ·{' '}
						{viewAsTarget.email} · {viewAsTarget.role} (#{viewAsTarget.id})
					</span>
				)}
				<div className="ms-auto d-flex flex-wrap align-items-center gap-2">
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						onClick={() => setOpen((o) => !o)}
						aria-expanded={open}
					>
						{viewAsTarget
							? copy('Change user', 'Benutzer wechseln', 'Promijeni korisnika')
							: copy('View as user…', 'Als Benutzer ansehen…', 'Pregledaj kao korisnik…')}
					</button>
					{viewAsTarget && (
						<button type="button" className="btn btn-sm btn-outline-dark" onClick={() => clearViewAs()}>
							{copy('Clear', 'Zurücksetzen', 'Poništi')}
						</button>
					)}
				</div>
			</div>
			{viewAsTarget && (
				<div className="d-flex flex-wrap align-items-center gap-2 mt-1 pt-1 border-top border-secondary-subtle">
					<span className="text-muted">{copy('Open their data', 'Deren Daten öffnen', 'Otvori njihove podatke')}:</span>
					{viewAsTarget.role === 'seeker' ? (
						<>
							<Link className="btn btn-sm btn-outline-primary py-0" to="/app/seeker/plans" onClick={() => setOpen(false)}>
								{copy('My Plans', 'Meine Pläne', 'Moji planovi')}
							</Link>
							<Link className="btn btn-sm btn-outline-primary py-0" to="/app/seeker/projects" onClick={() => setOpen(false)}>
								{copy('My Projects', 'Meine Projekte', 'Moji projekti')}
							</Link>
							<Link className="btn btn-sm btn-outline-primary py-0" to="/app/seeker/create-plan" onClick={() => setOpen(false)}>
								{copy('Create Plan', 'Plan erstellen', 'Novi plan')}
							</Link>
						</>
					) : viewAsTarget.role === 'company' || viewAsTarget.role === 'gardener' ? (
						<>
							<Link className="btn btn-sm btn-outline-primary py-0" to="/app/plans" onClick={() => setOpen(false)}>
								{copy('Marketplace', 'Marktplatz', 'Tržnica')}
							</Link>
							<Link className="btn btn-sm btn-outline-primary py-0" to="/app/projects" onClick={() => setOpen(false)}>
								{copy('Projects', 'Projekte', 'Projekti')}
							</Link>
							{viewAsTarget.role === 'company' && (
								<Link className="btn btn-sm btn-outline-primary py-0" to="/app/team" onClick={() => setOpen(false)}>
									{copy('Team', 'Team', 'Tim')}
								</Link>
							)}
							<Link className="btn btn-sm btn-outline-primary py-0" to="/app/schedule" onClick={() => setOpen(false)}>
								{copy('Schedule', 'Plan', 'Raspored')}
							</Link>
						</>
					) : null}
				</div>
			)}
			{loadError && <div className="text-danger mt-1">{loadError}</div>}
			{open && (
				<div className="mt-2 p-2 bg-white border rounded shadow-sm" style={{ maxHeight: 'min(380px, 55vh)', overflow: 'auto' }}>
					<div className="d-flex flex-wrap align-items-center gap-1 mb-2">
						<span className="text-muted me-1">
							{copy('Filter user list', 'Benutzerliste filtern', 'Filtriraj popis korisnika')}:
						</span>
						{(
							[
								['all' as const, copy('All', 'Alle', 'Sve')],
								['seeker' as const, copy('Seeker', 'Suchende', 'Vlasnik')],
								['gardener' as const, copy('Gardener', 'Gärtner', 'Vrtlar')],
								['company' as const, copy('Company', 'Firma', 'Tvrtka')],
								['admin' as const, copy('Admin', 'Admin', 'Admin')]
							] satisfies Array<['all' | UserResponse['role'], string]>
						).map(([value, label]) => (
							<button
								key={value}
								type="button"
								className={`btn btn-sm ${roleFilter === value ? 'btn-secondary' : 'btn-outline-secondary'}`}
								onClick={() => setRoleFilter(value)}
								aria-pressed={roleFilter === value}
							>
								{label}
							</button>
						))}
					</div>
					<input
						type="search"
						className="form-control form-control-sm mb-2"
						placeholder={copy('Search name, email, id…', 'Suche Name, E-Mail, ID…', 'Pretraži ime, e-mail, ID…')}
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						autoFocus
					/>
					<ul className="list-unstyled mb-0">
						{filtered.map((u) => (
							<li key={u.id}>
								<button
									type="button"
									className="btn btn-link btn-sm text-start text-decoration-none p-0 d-block w-100"
									onClick={() => pick(u)}
								>
									<span className="fw-medium">{u.name || u.email}</span>
									<span className="text-muted ms-1">
										({u.role} · #{u.id})
									</span>
									{u.email && u.name && <div className="text-muted small">{u.email}</div>}
								</button>
							</li>
						))}
						{filtered.length === 0 && (
							<li className="text-muted px-1 py-2">{copy('No matches', 'Keine Treffer', 'Nema rezultata')}</li>
						)}
					</ul>
				</div>
			)}
			{viewAsTarget && (
				<div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}>
					{copy(
						'Data across the app (plans, projects, tasks, calendar, team, PTO) loads for this user while you stay logged in as admin.',
						'Daten in der App (Pläne, Projekte, Aufgaben, Kalender, Team, Abwesenheit) werden für diesen Benutzer geladen, während Sie als Admin angemeldet bleiben.',
						'Podaci u aplikaciji učitavaju se za ovog korisnika dok ste prijavljeni kao admin.'
					)}
				</div>
			)}
		</div>
	);
}
